import { useState, useEffect } from "react";
import { RefreshCw, Trash2, X, Maximize2, Minimize2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useWidgetConfig } from "@/context/WidgetConfigContext";
import { cn } from "@/lib/utils";

interface ChatHeaderProps {
  onClose?: () => void;
  onRefresh?: () => void;
  onNewSession?: () => void;
  isRefreshing?: boolean;
  className?: string;
}

/**
 * Professional chat header with avatar, title, and actions
 */
export function ChatHeader({
  onClose,
  onRefresh,
  onNewSession,
  isRefreshing = false,
  className,
}: ChatHeaderProps) {
  const { config } = useWidgetConfig();
  const [showNewSessionDialog, setShowNewSessionDialog] = useState(false);

  // Get initials for avatar fallback
  const initials = config.name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const handleNewSession = () => {
    setShowNewSessionDialog(false);
    onNewSession?.();
  };

  // Track maximized state from parent window
  const [isMaximized, setIsMaximized] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [displayMode, setDisplayMode] = useState<"popup" | "sidebar">("popup");

  useEffect(() => {
    // Listen for state updates from parent window
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === "SMALLFORCE_WIDGET_STATE") {
        setIsMaximized(event.data.isMaximized);
        setIsMobile(event.data.isMobile);
        if (event.data.displayMode) {
          setDisplayMode(event.data.displayMode);
        }
      }
    };

    window.addEventListener("message", handleMessage);

    // Request current state from parent
    window.parent.postMessage({ type: "SMALLFORCE_GET_STATE" }, "*");

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const handleMaximize = () => {
    window.parent.postMessage({ type: "SMALLFORCE_MAXIMIZE" }, "*");
  };

  const handleRestore = () => {
    window.parent.postMessage({ type: "SMALLFORCE_RESTORE" }, "*");
  };

  return (
    <>
      <div
        className={cn("flex items-center gap-3 p-4 text-white", className)}
        style={{
          backgroundColor: "var(--widget-header-bg)",
          color: "var(--widget-header-text)",
        }}
      >
        {/* Logo/Avatar */}
        <div className="relative">
          <Avatar className="h-10 w-10 ring-2 ring-white/20">
            {config.logo ? (
              <AvatarImage src={config.logo} alt={config.name} />
            ) : null}
            <AvatarFallback
              className="font-semibold text-sm"
              style={{
                backgroundColor: "rgba(255,255,255,0.2)",
                color: "white",
              }}
            >
              {initials}
            </AvatarFallback>
          </Avatar>
        </div>

        {/* Title and description */}
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-base truncate">
            {config.header_title}
          </h2>
          {config.header_description && (
            <p className="text-sm opacity-80 truncate">
              {config.header_description}
            </p>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1">
          {/* Refresh button */}
          {onRefresh && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onRefresh}
              disabled={isRefreshing}
              className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/10 rounded-full"
            >
              <RefreshCw
                className={cn("h-4 w-4", isRefreshing && "animate-spin")}
              />
              <span className="sr-only">Refresh messages</span>
            </Button>
          )}

          {/* New session button */}
          {onNewSession && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowNewSessionDialog(true)}
              className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/10 rounded-full"
            >
              <Trash2 className="h-4 w-4" />
              <span className="sr-only">Start new conversation</span>
            </Button>
          )}

          {/* Close/Maximize button */}
          {onClose && (
            <>
              {isMobile || displayMode === "sidebar" ? (
                // Mobile or Sidebar mode: Always show close button
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/10 rounded-full"
                >
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close</span>
                </Button>
              ) : isMaximized ? (
                // Desktop popup maximized: Show restore button
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleRestore}
                  className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/10 rounded-full"
                >
                  <Minimize2 className="h-4 w-4" />
                  <span className="sr-only">Restore</span>
                </Button>
              ) : (
                // Desktop popup normal: Show maximize button
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleMaximize}
                  className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/10 rounded-full"
                >
                  <Maximize2 className="h-4 w-4" />
                  <span className="sr-only">Maximize</span>
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* New Session Confirmation Dialog */}
      <AlertDialog
        open={showNewSessionDialog}
        onOpenChange={setShowNewSessionDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Start a new conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              This will clear your current conversation and start fresh. Your
              previous messages will still be saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleNewSession}
              className="text-white! hover:opacity-90!"
              style={{
                backgroundColor: config.primary_color,
              }}
            >
              Start new
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
